import React, { useState, useEffect, useMemo } from 'react';
import { XMarkIcon, SparklesIcon } from '@heroicons/react/24/outline';
import request from '../utils/request';
import { requestAIWithCache } from '../utils/requestAIWithCache';



interface AIGenerateResponse {
  code: string;
  src: string;
  status: string;
}

interface AIModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedLayerId: string | null;
  selectedLayerUrl?: string;
  selectedAICode?: string;
  onGenerateAI: (styleModel: string, description: string) => void;
  onAIGenerated: (data: AIGenerateResponse) => void;
}

const AIModal: React.FC<AIModalProps> = ({
  isOpen,
  onClose,
  selectedLayerId,
  selectedLayerUrl,
  selectedAICode,
  onGenerateAI,
  onAIGenerated
}) => {
  const [selectedStyle, setSelectedStyle] = useState('realistic');
  const [description, setDescription] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [styles, setStyles] = useState<Array<{ styleName: string; prompt: string }>>([]);

  // 根据当前选中的样式，决定是否显示描述框（当该样式的 prompt 非空时显示）
  const showDescription = useMemo(() => {
    const found = styles.find(s => s.styleName === selectedStyle);
    return !!(found && found.prompt && found.prompt.trim());
  }, [styles, selectedStyle]);

 
  // 初始化时拉取样式列表（仅一次）
  useEffect(() => {
    const fetchStyles = async () => {
      try {
        const resp = await request({ url: '/ai/style', method: 'GET' });
        if (resp && resp.success && resp.result && Array.isArray(resp.result.styles)) {
          const list = resp.result.styles as Array<{ styleName: string; prompt: string }>;
          setStyles(list);
          // 如果当前选中样式不在列表中，则默认选中第一个
          if (list.length > 0 && !list.some(s => s.styleName === selectedStyle)) {
            setSelectedStyle(list[0].styleName);
            // 如果该样式有默认 prompt，则使用它作为描述的初始值
            if (list[0].prompt && list[0].prompt.trim()) {
              setDescription(list[0].prompt);
            } else {
              setDescription('');
            }
          }
        }
      } catch (err) {
        console.error('Error fetching AI styles:', err);
      }
    };
    fetchStyles();
  }, []);

  const fetchAIInfo = async (aiCode: string) => {
    try {
      const response = await request({
        url: `/ai/info?code=${aiCode}`,
        method: 'GET'
      });
      
      if (response.success && response.result) {
        setSelectedStyle(response.result.styleModel || 'realistic');
        setDescription(response.result.description || '');
      }
    } catch (error) {
      console.error('Error fetching AI info:', error);
    }
  };

  // 打开时根据 selectedAICode 拉取 AI 信息
  useEffect(() => {
    if (isOpen && selectedAICode) {
      fetchAIInfo(selectedAICode);
    }
  }, [isOpen, selectedAICode]);

  if (!isOpen) return null;

  const handleGenerate = async () => {
    // 清除缓存测试
    // localStorage.setItem('GiftMkr_ai_cache:{"code":"Pencil-Sketch","url":"https://mobuffer.oss-us-west-1.aliyuncs.com//97/97Y2545I4.png"}','');
    // 如果需要描述且当前描述为空，则不执行
    if (showDescription && !description.trim()) return;
    // 必须提供图层图片 URL
    if (!selectedLayerUrl || !selectedLayerUrl.trim()) {
      console.error('No layer image URL provided for AI generation');
      return;
    }
    
    setIsLoading(true);
    try {
      
      // Call the AI generation API
      const requestdata: any = {
        styleModel: selectedStyle,
        description: description,
        url: selectedLayerUrl
      }; 
      // 如果有 selectedAICode，添加 code 参数
      if (selectedAICode) {
        requestdata.code = selectedAICode;
      }
      const res2 = await request({
        url: '../back/giftmkr/ai/requestAI',
        method: 'POST',
        data: requestdata,
      }); 
      const dataStr = JSON.stringify({"code":res2.result.code,"url":selectedLayerUrl});
      const res = await requestAIWithCache(dataStr); 
      // Handle the response data
      if (res) { 
        onAIGenerated(res);
        setDescription('');
        onClose();
      } else {
        console.error('Invalid response format from AI API');
      }
    } catch (error) {
      console.error('Error generating AI content:', error);
      // You might want to show an error message to the user here
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1000] p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center space-x-2">
            <SparklesIcon className="w-5 h-5 text-purple-600" />
            <h3 className="text-lg font-semibold text-gray-800">AI Enhancement {selectedLayerId}</h3>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-full"
            disabled={isLoading}
          >
            <XMarkIcon className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4"> 
          {!selectedLayerId ? (
            <div className="text-center py-8">
              <SparklesIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">Please select an image or frame layer to use AI enhancement.</p>
            </div>
          ) : (
            <>
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700">AI Style Model</label>
                <select
                  value={selectedStyle}
                  onChange={(e) => {
                    const next = e.target.value;
                    setSelectedStyle(next);
                    const matched = styles.find(s => s.styleName === next);
                    if (matched) {
                      if (matched.prompt && matched.prompt.trim()) {
                        setDescription(matched.prompt);
                      } else {
                        setDescription('');
                      }
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  disabled={isLoading}
                >
                  {styles.length > 0 ? (
                    styles.map((s) => (
                      <option key={s.styleName} value={s.styleName}>{s.styleName}</option>
                    ))
                  ) : (
                    <> 
                    </>
                  )}
                </select>
              </div>
              
              {showDescription && (
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700">AI Description </label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Describe what you want to generate or modify..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none"
                    rows={4}
                    disabled={isLoading}
                  />
                </div>
              )}
              
              <div className="flex space-x-3 pt-2">
                <button
                  onClick={onClose}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  disabled={isLoading}
                >
                  Cancel
                </button>
                <button
                  onClick={handleGenerate}
                  disabled={isLoading || (showDescription && !description.trim())}
                  className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {isLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Generating...
                    </>
                  ) : (
                    'Generate with AI'
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AIModal;